import type {
  ElasticSearchHit,
  ElasticSearchResponse,
} from '@src/models/interfaces/general/ElasticSearchResponseInterface';

export class ElasticResponseMapper {
  static fromHit<TSource, TTarget>(
    hit: ElasticSearchHit<TSource>,
    mapFn: (source: TSource, id: string) => TTarget,
  ): TTarget {
    const source = (hit._source ?? {}) as TSource;
    return mapFn(source, hit._id);
  }

  static fromResponse<TSource, TTarget>(
    response: ElasticSearchResponse<TSource> | null,
    mapFn: (source: TSource, id: string) => TTarget,
  ): { items: TTarget[]; total: number } {
    if (!response) {
      return { items: [], total: 0 };
    }

    const hits = response.hits?.hits ?? [];
    const items = hits.map(hit => ElasticResponseMapper.fromHit(hit, mapFn));
    const total = response.hits?.total?.value ?? items.length;

    return { items, total };
  }

  static createMapper<TSource, TTarget>(
    transformFn: (source: TSource, id: string) => TTarget,
  ): (source: TSource, id: string) => TTarget {
    return transformFn;
  }

  static fromHitsBatch<TSource, TTarget>(
    hits: ElasticSearchHit<TSource>[],
    mapFn: (source: TSource, id: string) => TTarget,
    onError?: (error: Error, hit: ElasticSearchHit<TSource>) => void,
  ): TTarget[] {
    return hits
      .map(hit => {
        try {
          return { success: true, value: ElasticResponseMapper.fromHit(hit, mapFn) };
        } catch (error) {
          if (onError) {
            onError(error instanceof Error ? error : new Error(String(error)), hit);
            return { success: false, value: null };
          }
          throw error;
        }
      })
      .filter((result): result is { success: true; value: TTarget } => result.success)
      .map(result => result.value);
  }
}
